import type { BulkImport, BulkImportType } from "@gettactic/api";
import type { PaginatedResult } from "@gettactic/api/src/endpoints/response/paginated_result";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTacticHooks } from "../../providers/TacticHooksProvider";

export const BULK_IMPORTS_QUERY_KEY = "useBulkImportsQuery";

const PAGE_SIZE = 20;
const ACTIVE_POLL_INTERVAL_MS = 5000;

export const useBulkImportsQuery = (type?: BulkImportType) => {
  const { apiClient } = useTacticHooks();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery<PaginatedResult<BulkImport> | null>({
      queryKey: [BULK_IMPORTS_QUERY_KEY, apiClient.slug, type],
      queryFn: async ({ pageParam }) => {
        const res = await apiClient.bulkImports.list(
          pageParam as string | undefined,
          PAGE_SIZE,
          type
        );
        return res.result;
      },
      initialPageParam: undefined,
      getNextPageParam: (lastPage) =>
        (lastPage?.elements.length ?? 0) < PAGE_SIZE
          ? undefined
          : (lastPage?.cursor ?? undefined),
      enabled: !!apiClient.slug,
      refetchInterval: (query) => {
        const elements = (query.state.data?.pages ?? []).flatMap(
          (page) => page?.elements ?? []
        );
        const hasActive = elements.some(
          (item) => item.status === "queued" || item.status === "processing"
        );
        return hasActive ? ACTIVE_POLL_INTERVAL_MS : false;
      }
    });

  const allImports = useMemo(
    () => (data?.pages ?? []).flatMap((page) => page?.elements ?? []),
    [data]
  );

  return {
    allImports,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading
  };
};
